import { useEffect, useMemo, useRef, useState, type ReactNode, type Ref } from 'react';
import { cn, FeedbackPopup, MarkdownMath } from '@jeraisonne/ui';
import { BookOpenCheck, Sparkles } from 'lucide-react';
import { ChoiceButton } from '@/components/exercise/choice-button';
import {
  AnswerDock,
  AnswerKeys,
  PaperChoiceList,
  choiceKeyLayout,
  type PaperQcm,
} from '@jeraisonne/ui/paper';
import { usePaperOverride } from '@/features/paper-player';
import { getFallbackContent } from '@/features/demo/demo-data';
import type { DemoStep } from '@/features/demo/types';
import { usePrefersReducedMotion } from './use-prefers-reduced-motion';
import {
  advanceAutoplay,
  AUTOPLAY_PHASE_MS,
  gamificationUpTo,
  INITIAL_AUTOPLAY_STATE,
  pickedChoiceIndex,
  REDUCED_AUTOPLAY_STATE,
  type AutoplayState,
} from './decouverte-autoplay';

/**
 * Colonne gauche de `/decouverte` en desktop (≥ lg) : un aperçu d'exercice joué
 * TOUT SEUL, DANS un mock de téléphone — question → sélection → révélation +
 * popup de feedback « gamy », les pas précédents restant EMPILÉS au-dessus
 * (comme le vrai player). En boucle. Remplace le bandeau de marque (qui répétait
 * le héros du carrousel) par une démonstration vivante du produit.
 *
 * Décoratif : tout le sous-arbre est `inert` (hors tabulation, hors a11y) — le
 * carrousel de droite reste la version interactive et accessible.
 *
 * Contenu 100 % statique (`DEMO_FALLBACK_CONTENT`) → marche avant toute
 * connexion, sans réseau. L'enchaînement est pur et testé (`decouverte-autoplay.ts`).
 */

// Exercice mis en avant : un classique du Brevet, cheminement en 5 étapes.
const DEMO = getFallbackContent('3e').exercise;
// Pas où l'on « se trompe » volontairement pour montrer le feedback d'erreur.
const MISTAKE_STEP_INDEX = 3;

// Dimensions du mock téléphone (ratio iPhone ~19.5/9), rendu réduit pour tenir
// dans la colonne. Le `transform: scale` confine aussi le `fixed` de la popup
// au cadre du téléphone (cf. PhoneFrame).
const FRAME_W = 384;
const FRAME_H = Math.round((FRAME_W * 19.5) / 9); // ≈ 832
const SCALE = 0.78;

export function DecouverteAutoplayer() {
  const reduced = usePrefersReducedMotion();
  const total = DEMO.steps.length;
  const [state, setState] = useState<AutoplayState>(INITIAL_AUTOPLAY_STATE);
  // feuille par défaut ; `?paper=0` remet l'ancien rendu sur l'appareil
  const paper = usePaperOverride() !== false;
  const scrollRef = useRef<HTMLDivElement>(null);

  // Bonne/mauvaise réponse déterministe par pas (choix figé) → gamification pure.
  const wasCorrectByStep = useMemo(
    () =>
      DEMO.steps.map((step, i) => {
        const idx = pickedChoiceIndex(step, i, MISTAKE_STEP_INDEX);
        return step.choices[idx]?.is_correct ?? true;
      }),
    [],
  );

  // Mouvement réduit : on fige un pas révélé (avec un peu d'historique) au lieu
  // de dérouler en boucle.
  useEffect(() => {
    if (reduced) setState(REDUCED_AUTOPLAY_STATE);
  }, [reduced]);

  // Boucle d'autoplay (désactivée en mouvement réduit).
  useEffect(() => {
    if (reduced) return;
    const id = window.setTimeout(
      () => setState((s) => advanceAutoplay(s, total)),
      AUTOPLAY_PHASE_MS[state.phase],
    );
    return () => window.clearTimeout(id);
  }, [state, total, reduced]);

  // Suit le bas du flux (comme le vrai player) : le haut reste ancré en haut du
  // téléphone, seul le contenu défile à l'intérieur du cadre à taille fixe.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [state.stepIndex, state.phase]);

  const step = DEMO.steps[state.stepIndex];
  if (!step) return null;

  const pickedIndex = pickedChoiceIndex(step, state.stepIndex, MISTAKE_STEP_INDEX);
  const pickedChoice = step.choices[pickedIndex];
  const selected = state.phase === 'selecting' || state.phase === 'revealed';
  const revealed = state.phase === 'revealed';
  const pickedWasCorrect = pickedChoice?.is_correct ?? true;
  const { reward, sessionXp } = gamificationUpTo(wasCorrectByStep, state.stepIndex);
  const progress = ((state.history.length + (revealed ? 1 : 0)) / total) * 100;
  const showPopup = revealed && !reduced;
  // Exercice « sur la feuille » : la touche choisie s'allume, puis la réponse
  // se révèle (même déroulé que les cartes).
  const paperOptions = step.choices.map((c) => ({
    id: c.id,
    label: c.label_md,
    visuals: [],
    is_correct: c.is_correct,
  }));
  const paperQcm: PaperQcm = {
    options: paperOptions,
    multiple: false,
    layout: choiceKeyLayout(
      paperOptions.map((o) => ({ id: o.id, label: o.label, hasVisuals: false })),
    ),
    selected: selected && !revealed && pickedChoice ? [pickedChoice.id] : [],
    chosen: revealed && pickedChoice ? [pickedChoice.id] : [],
    revealed,
    shown: true,
    busy: false,
    toggle: () => {},
    check: () => {},
  };

  return (
    <div
      // `inert` (React 19) : sous-arbre non focusable, retiré de l'a11y — c'est
      // un aperçu décoratif, pas un vrai player.
      inert
      className="flex flex-col items-center gap-4"
    >
      {/* Étiquette lisible sur le fond BLANC de la colonne gauche (quel que soit
          le mode de l'app → on force des couleurs littérales ici). */}
      <div className="flex items-center gap-2 text-[#0d4491]">
        <Sparkles className="h-4 w-4 text-[#f4c64e]" aria-hidden="true" />
        <span className="text-sm font-semibold">Aperçu en direct</span>
      </div>

      <PhoneFrame scrollRef={scrollRef}>
        <div className="space-y-2.5 px-3 pb-8 pt-5">
          <TitleCard stepNumber={state.stepIndex + 1} total={total} progress={progress} />

          {/* Historique accumulé — les pas précédents restent visibles. */}
          {state.history.map((histIndex, i) => (
            <AnsweredCard
              key={`${histIndex}-${i}`}
              step={DEMO.steps[histIndex]!}
              index={i}
              wasCorrect={wasCorrectByStep[histIndex] ?? true}
            />
          ))}

          {/* Étape courante. */}
          <div className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4">
            <div className="text-ink-400 text-[11px] font-semibold uppercase tracking-wide">
              Étape {state.stepIndex + 1}
            </div>
            <div className="text-ink-900 text-sm leading-relaxed">
              <MarkdownMath>{step.prompt_md}</MarkdownMath>
            </div>
            {paper ? (
              <PaperChoiceList qcm={paperQcm} />
            ) : (
              <div className="space-y-2">
                {step.choices.map((choice, i) => (
                  <ChoiceButton
                    key={choice.id}
                    choice={choice}
                    index={i}
                    selected={selected && i === pickedIndex}
                    revealed={revealed}
                    onClick={() => {}}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        {paper && (
          <AnswerDock label="Ta réponse" fixed={false} className="sticky bottom-0">
            <AnswerKeys qcm={paperQcm} keyboard={false} />
          </AnswerDock>
        )}

        {showPopup && (
          <FeedbackPopup
            reward={reward}
            wasCorrect={pickedWasCorrect}
            sessionXp={sessionXp}
            explanationMd={pickedWasCorrect ? undefined : step.explanation_md}
            errorFeedbackMd={
              pickedWasCorrect ? undefined : (pickedChoice?.feedback_md ?? undefined)
            }
            onClose={() => {}}
            autoFocusContinue={false}
            // Overlay confiné à l'écran : voile léger, en retrait du liseré de 6px
            // + rayon intérieur (2.25rem − 6px) pour épouser les coins arrondis.
            overlayClassName="inset-[6px] bg-black/25 rounded-[1.875rem]"
          />
        )}
      </PhoneFrame>
    </div>
  );
}

// ─── Mock téléphone ───────────────────────────────────────────────────────────

/**
 * Cadre téléphone à taille FIXE (donc stable : le contenu défile à l'intérieur,
 * la carte ne « saute » plus quand son contenu grandit). Le `transform: scale`
 * sur le calque interne établit le bloc conteneur du `fixed` de la popup → celle-ci
 * se confine au téléphone au lieu de couvrir tout l'écran.
 */
function PhoneFrame({
  children,
  scrollRef,
}: {
  children: ReactNode;
  scrollRef: Ref<HTMLDivElement>;
}) {
  return (
    <div className="mx-auto" style={{ width: FRAME_W * SCALE, height: FRAME_H * SCALE }}>
      <div
        className="origin-top-left"
        style={{ width: FRAME_W, height: FRAME_H, transform: `scale(${SCALE})` }}
      >
        <div className="relative h-full overflow-hidden rounded-[2.25rem] border-[6px] border-[#1b2a44] shadow-2xl">
          {/* Fond de l'app dans le mode courant (clair / sombre, cf. lib/app-theme.ts). */}
          <div
            aria-hidden="true"
            className="ocean:bg-[linear-gradient(180deg,#071a40_0%,#040e27_100%)] absolute inset-0 bg-[linear-gradient(180deg,#eef5ff_0%,#f7faff_100%)]"
          />
          {/* Encoche stylisée, au-dessus de l'overlay de la popup. */}
          <div className="z-80 absolute left-1/2 top-1.5 h-5 w-24 -translate-x-1/2 rounded-b-2xl bg-[#05060a]" />
          {/* Écran défilant — top-aligné (le haut reste en haut). */}
          <div ref={scrollRef} className="relative h-full overflow-y-auto overscroll-contain">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Cartes ───────────────────────────────────────────────────────────────────

function TitleCard({
  stepNumber,
  total,
  progress,
}: {
  stepNumber: number;
  total: number;
  progress: number;
}) {
  return (
    <div className="border-ink-200 space-y-2 rounded-2xl border bg-white p-4">
      <p className="text-primary-600 text-[11px] font-bold uppercase tracking-wider">
        {DEMO.chapter_label}
      </p>
      <h3 className="font-display text-ink-900 text-base font-extrabold leading-tight">
        {DEMO.title}
      </h3>
      <div className="text-ink-400 flex items-center gap-x-3 text-[11px] font-semibold">
        <span className="inline-flex items-center gap-1.5">
          <BookOpenCheck className="text-primary-500 h-3.5 w-3.5" /> Exercice guidé
        </span>
        <span>
          Étape {Math.min(stepNumber, total)}/{total}
        </span>
      </div>
      <div className="bg-ink-100 h-1.5 w-full overflow-hidden rounded-full">
        <div
          className="bg-primary-400 h-full rounded-full transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

/** Carte d'un pas déjà répondu — calquée sur `AnsweredStepCard` du player démo. */
function AnsweredCard({
  step,
  index,
  wasCorrect,
}: {
  step: DemoStep;
  index: number;
  wasCorrect: boolean;
}) {
  const correctChoice = step.choices.find((c) => c.is_correct);
  return (
    <div className="border-ink-100 relative space-y-2 rounded-2xl border bg-white p-3.5 pr-11">
      <div className="flex items-center gap-1.5">
        <span className="text-ink-400 text-[11px] font-semibold uppercase tracking-wide">
          Étape {index + 1}
        </span>
        <span
          className={cn(
            'text-[11px] font-semibold uppercase tracking-wide',
            wasCorrect ? 'text-xp-600' : 'text-error-600',
          )}
        >
          {wasCorrect ? '· Réussie' : '· À revoir'}
        </span>
      </div>
      <div className="text-ink-800 line-clamp-2 text-[13px]">
        <MarkdownMath>{step.prompt_md}</MarkdownMath>
      </div>
      {correctChoice && (
        <div className="flex items-start gap-2">
          <span className="text-xp-600 mt-0.5 shrink-0 text-xs font-bold">✓</span>
          <div className="text-xp-700 flex-1 text-[13px]">
            <MarkdownMath>{correctChoice.label_md}</MarkdownMath>
          </div>
        </div>
      )}
      <span
        aria-hidden="true"
        className={cn(
          'absolute bottom-3 right-3 flex h-7 w-7 items-center justify-center text-xl font-extrabold',
          wasCorrect ? 'text-xp-500' : 'text-error-500',
        )}
      >
        {wasCorrect ? '✓' : '✗'}
      </span>
    </div>
  );
}
